import {
    collection,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc
} from "firebase/firestore";

import {db} from "../../Firebase";
import {
    kCompositionsCollectionName,
    kCompositionLayersSubcollectionName,
    kCompositionLayerClipsSubcollectionName
} from "../../utils/CollectionConstants";
import {CompositionStateStore} from "../store/CompositionStateStore";
import { buildReferencedAssetIds } from "../../storage/AssetReferenceUsage";
import type {
    ScriptLayerCut,
    ScriptLayerGap,
    ScriptLayerGapInsertion,
    ScriptLayerSplit,
    SpeakerOverride,
    SpeakerMuteRange,
    TranscriptHiddenRange,
    TranscriptWordCorrection,
    TranscriptParagraphBreak,
    TranscriptMarker,
    SceneProperties,
    SceneVisualProperties,
    TransitionConfig,
} from "../../engine/clips/script/script.interfaces";

// Helper: get current time as Unix seconds (int64-compatible for Go server)
const nowUnixSec = () => Math.floor(Date.now() / 1000);
const PERSISTED_SCHEMA_VERSION = 1;

type EngineClipLike = any;

export type ExportSnapshotClipRecord = {
    id: string;
    layerId: string;
    type: string;
    name?: string;
    assetId?: string | null;
    thumbnailId?: string | null;
} & Record<string, any>;

/**
 * Source configuration for a ScriptLayerClip - what we persist to Firestore.
 * These are the serialized versions of ScriptLayerInputConfig.
 */
export interface PersistedScriptSource {
    assetId: string;
    name?: string;
    color?: string;
    gain: number;
    playbackRate: number;
    offsetSec: number;
    range?: { inSec: number; outSec: number };
    visual?: {
        x: number;
        y: number;
        width: number;
        height: number;
        alpha: number;
        crop?: { x: number; y: number; width: number; height: number };
        originalWidth?: number;
        originalHeight?: number;
    };
    layerIndex?: number;
}

/**
 * Firestore persistence for Composition and its Clips.
 * - Call init() with userId and compositionId when opening a composition.
 * - Methods are idempotent/upserts where appropriate.
 *
 * Schema:
 * compositions/{compositionId}
 *   ├── compositionId, projectId, userId, title, deleted, createdAt
 *   ├── canvasWidth, canvasHeight, backgroundColor
 *   ├── orderedLayerIds: string[]
 *   └── layers/{layerId}
 *       ├── id, compositionId, userId, name, type, order, createdAt
 *       ├── orderedClipIds: string[]
 *       └── clips/{clipId}
 *           ├── Basic: id, type, name, assetId, thumbnailId, layerId, compositionId, userId
 *           ├── Visual: visual (x,y,width,height,rotation,scale,crop)
 *           ├── Timing: startSec, inSec, outSec, offsetSec, durationSec, trimmedDurationSec
 *           ├── Props: gain, playbackRate, alpha
 *           └── ScriptLayerClip extended fields:
 *               ├── scriptCuts: ScriptLayerCut[]
 *               ├── gaps: ScriptLayerGap[]
 *               ├── splits: ScriptLayerSplit[]
 *               ├── speakerOverrides: SpeakerOverride[]
 *               ├── speakerMuteRanges: SpeakerMuteRange[]
 *               ├── sources: PersistedScriptSource[]
 *               └── sceneProperties: PersistedSceneProperties[]
 */
export class CompositionPersistenceService {
    private static toFirestoreSceneKey(key: string): string {
        // Firestore reserves field names that begin and end with "__".
        // Internally we use "__start__" for the first scene; store it safely.
        if (key === "__start__") return "start";
        return key;
    }

    private static activeCompositionId: string | null = null;
    private static userId: string | null = null;
    private static workspaceId: string | null = null;
    private static initialized: boolean = false;
    private static isTearingDown: boolean = false;
    private static readOnly: boolean = false;
    private static didMarkNonEmpty: boolean = false;

    /**
     * Initialize the service with the current user and composition.
     * Must be called before any persistence operations.
     */
    static init(
        userId: string | null,
        compositionId: string | null,
        workspaceId?: string | null,
        readOnly: boolean = false
    ): void {
        if (!userId) {
            console.warn('[CompositionPersistenceService] init called without userId');
        }
        this.userId = userId || null;
        this.activeCompositionId = compositionId || null;
        this.workspaceId = workspaceId || null;
        this.initialized = true;
        this.isTearingDown = false;
        this.readOnly = readOnly;
        this.didMarkNonEmpty = false;
        console.log('[CompositionPersistenceService] Initialized', {
            userId: this.userId,
            workspaceId: this.workspaceId,
            compositionId: this.activeCompositionId
        });
    }

    /**
     * Set teardown mode. When true, all persistence operations are skipped.
     * Use this when clearing state during composition switch.
     */
    static setTearingDown(tearing: boolean): void {
        this.isTearingDown = tearing;
    }

    static setReadOnly(readOnly: boolean): void {
        this.readOnly = readOnly;
    }

    /**
     * Reset the service state. Call when leaving a composition.
     */
    static reset(): void {
        this.userId = null;
        this.activeCompositionId = null;
        this.workspaceId = null;
        this.initialized = false;
        this.isTearingDown = false;
        this.readOnly = false;
        this.didMarkNonEmpty = false;
        // Clear any pending operations
        this.pendingLayerDeletes.clear();
        this.pendingClipDeletes.clear();
    }

    /**
     * Check if the service is properly initialized.
     */
    static isInitialized(): boolean {
        return this.initialized && this.userId !== null && this.activeCompositionId !== null;
    }

    // ============ Composition-level Operations ============

    /**
     * Mark the composition record as no longer "new/empty".
     * This is intended as a one-way flag used to skip restore for brand-new compositions.
     */
    private static async markCompositionNonEmptyOnce(): Promise<void> {
        if (this.didMarkNonEmpty) return;
        if (!this.activeCompositionId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) return;
        try {
            const compRef = doc(db, kCompositionsCollectionName, this.activeCompositionId);
            await setDoc(
                compRef,
                {
                    compositionId: this.activeCompositionId,
                    userId: this.userId,
                    ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                    schemaVersion: PERSISTED_SCHEMA_VERSION,
                    updatedAt: nowUnixSec(),
                    isEmpty: false,
                },
                { merge: true }
            );
            this.didMarkNonEmpty = true;
        } catch (e) {
            console.error("[CompositionPersistenceService] Error marking composition non-empty:", e);
        }
    }

    /**
     * Persist the ordered layer IDs for the composition.
     * Creates the composition document if it doesn't exist.
     */
    static async setOrderedLayerIds(orderedLayerIds: string[]): Promise<void> {
        if (!this.activeCompositionId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping setOrderedLayerIds while writes are disabled");
            return;
        }
        try {
            const compRef = doc(db, kCompositionsCollectionName, this.activeCompositionId);
            await setDoc(
                compRef,
                {
                    userId: this.userId,
                    ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                    schemaVersion: PERSISTED_SCHEMA_VERSION,
                    updatedAt: nowUnixSec(),
                    orderedLayerIds: orderedLayerIds,
                },
                {merge: true}
            );
        } catch (e) {
            console.error('[CompositionPersistenceService] Error setting ordered layer IDs:', e);
        }
    }

    /**
     * Persist the ordered clip IDs for a single layer.
     * This enables restore without listing the clips subcollection.
     */
    static async setOrderedClipIds(layerId: string, orderedClipIds: string[]): Promise<void> {
        if (!this.activeCompositionId || !this.userId || !layerId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping setOrderedClipIds while writes are disabled");
            return;
        }

        const cleaned = Array.isArray(orderedClipIds)
            ? orderedClipIds.filter((id: any) => typeof id === 'string' && id.length > 0)
            : [];

        const nowSec = nowUnixSec();

        try {
            const layerRef = doc(
                db,
                kCompositionsCollectionName, this.activeCompositionId,
                kCompositionLayersSubcollectionName, layerId,
            );

            try {
                await updateDoc(layerRef, {
                    updatedAt: nowSec,
                    orderedClipIds: cleaned,
                });
            } catch (e: any) {
                // If the layer doc doesn't exist yet, create it.
                if (e?.code === 'not-found') {
                    await setDoc(layerRef, {
                        id: layerId,
                        compositionId: this.activeCompositionId,
                        userId: this.userId,
                        ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                        schemaVersion: PERSISTED_SCHEMA_VERSION,
                        createdAt: nowSec,
                        updatedAt: nowSec,
                        orderedClipIds: cleaned,
                    }, {merge: true});
                } else {
                    throw e;
                }
            }
        } catch (e) {
            console.error('[CompositionPersistenceService] Error setting ordered clip IDs:', {compositionId: this.activeCompositionId, layerId}, e);
        }
    }

    /**
     * Persist canvas/display settings.
     * Creates the composition document if it doesn't exist.
     */
    static async updateDisplaySettings(settings: {
        width?: number;
        height?: number;
        backgroundColor?: string
    }): Promise<void> {
        if (!this.activeCompositionId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping updateDisplaySettings while writes are disabled");
            return;
        }
        try {
            const compRef = doc(db, kCompositionsCollectionName, this.activeCompositionId);
            const updates: Record<string, any> = {
                compositionId: this.activeCompositionId,
                userId: this.userId,
                ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                schemaVersion: PERSISTED_SCHEMA_VERSION,
                updatedAt: nowUnixSec()
            };
            if (settings.width !== undefined) updates.canvasWidth = settings.width;
            if (settings.height !== undefined) updates.canvasHeight = settings.height;
            if (settings.backgroundColor !== undefined) updates.backgroundColor = settings.backgroundColor;
            // Use setDoc with merge to create if doesn't exist
            await setDoc(compRef, updates, {merge: true});
        } catch (e) {
            console.error('[CompositionPersistenceService] Error updating display settings:', e);
        }
    }

    // ============ Layer Operations ============

    /**
     * Add a new layer to the composition.
     * Creates the parent composition document if it doesn't exist.
     */
    static async addLayer(layerId: string, name?: string, type?: string): Promise<void> {
        if (!this.activeCompositionId || !layerId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping addLayer while writes are disabled");
            return;
        }
        const nowSec = nowUnixSec();
        const trackRef = doc(collection(doc(db, kCompositionsCollectionName, this.activeCompositionId), kCompositionLayersSubcollectionName), layerId);
        await setDoc(trackRef, {
            id: layerId,
            compositionId: this.activeCompositionId,
            updatedAt: nowSec,
            userId: this.userId,
            ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
            schemaVersion: PERSISTED_SCHEMA_VERSION,
            createdAt: nowSec,
            name: name || null,
        }, {merge: true});
    }

    /**
     * Update a layer's name.
     */
    static async updateLayerName(layerId: string, name: string): Promise<void> {
        if (!this.activeCompositionId || !layerId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) return;
        const layerRef = doc(
            collection(doc(db, kCompositionsCollectionName, this.activeCompositionId), kCompositionLayersSubcollectionName),
            layerId
        );
        await updateDoc(layerRef, {
            name,
            updatedAt: nowUnixSec(),
        });
    }

    // Track pending deletes to prevent duplicate calls
    private static pendingLayerDeletes = new Set<string>();

    /**
     * Delete a layer from the composition.
     * Silently succeeds if the layer doesn't exist in Firestore.
     */
    static async deleteLayer(layerId: string): Promise<void> {
        if (!this.activeCompositionId || !layerId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping layer delete while writes are disabled:", layerId);
            return;
        }

        const deleteKey = `${this.activeCompositionId}/${layerId}`;
        if (this.pendingLayerDeletes.has(deleteKey)) {
            console.log("[CompositionPersistenceService] Duplicate layer delete ignored:", layerId);
            return;
        }
        this.pendingLayerDeletes.add(deleteKey);

        const trackRef = doc(collection(doc(db, kCompositionsCollectionName, this.activeCompositionId), kCompositionLayersSubcollectionName), layerId);
        
        try {
            await deleteDoc(trackRef);
            console.log("[CompositionPersistenceService] Layer deleted successfully:", layerId);
        } catch (e: any) {
            // Silently ignore permission errors for non-existent docs
            if (e?.code === 'permission-denied' || e?.message?.includes('permission')) {
                console.log('[CompositionPersistenceService] Layer not in Firestore, skipping delete:', layerId);
                return;
            }
            console.error("[CompositionPersistenceService] Error deleting layer:", e);
        } finally {
            this.pendingLayerDeletes.delete(deleteKey);
        }
    }

    // ============ Clip Operations ============

    /**
     * Add a clip to a layer.
     * For ScriptLayerClips, also persists cuts, gaps, splits, and sources.
     * Creates parent composition and layer documents if they don't exist.
     */
    static async addClip(clip: EngineClipLike, layerId: string): Promise<void> {
        if (!this.activeCompositionId || !clip?.id || !layerId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping addClip while writes are disabled");
            return;
        }
        // Once we have any clip to persist, this composition is no longer "new/empty".
        await this.markCompositionNonEmptyOnce();
        const nowSec = nowUnixSec();
        const clipRef = doc(collection(doc(collection(doc(db, kCompositionsCollectionName, this.activeCompositionId), kCompositionLayersSubcollectionName), layerId), kCompositionLayerClipsSubcollectionName), clip.id);

        const assetId = this.lookupAssetId(clip.id);
        const thumbnailId = this.lookupThumbnailId(clip.id);
        const mapped = this.mapClipForStorage(clip);
        const scriptFields = this.mapScriptClipFields(clip);
        const referencedAssetIds = buildReferencedAssetIds({
            assetId,
            sources: Array.isArray(scriptFields.sources) ? scriptFields.sources : undefined,
        });

        await setDoc(clipRef, {
            id: clip.id,
            compositionId: this.activeCompositionId,
            layerId,
            type: String(clip.type ?? "unknown"),
            assetId: assetId ?? null,
            thumbnailId: thumbnailId ?? null,
            name: clip.name ?? "",
            userId: this.userId,
            ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
            schemaVersion: PERSISTED_SCHEMA_VERSION,
            createdAt: nowSec,
            updatedAt: nowSec,
            referencedAssetIds,
            ...mapped,
            ...scriptFields,
        }, {merge: true});
    }

    /**
     * Update an existing clip.
     * Uses setDoc with merge so it creates the document if it doesn't exist.
     */
    static async updateClip(clip: EngineClipLike): Promise<void> {
        if (!this.activeCompositionId || !clip?.id || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping updateClip while writes are disabled");
            return;
        }
        // Once we have any clip to persist, this composition is no longer "new/empty".
        await this.markCompositionNonEmptyOnce();
        const layerId: string = String(clip?.track?.id ?? "");
        if (!layerId) return;
        const clipRef = doc(collection(doc(collection(doc(db, kCompositionsCollectionName, this.activeCompositionId), kCompositionLayersSubcollectionName), layerId), kCompositionLayerClipsSubcollectionName), clip.id);

        const assetId = this.lookupAssetId(clip.id);
        const thumbnailId = this.lookupThumbnailId(clip.id);
        const mapped = this.mapClipForStorage(clip);
        const scriptFields = this.mapScriptClipFields(clip);
        const referencedAssetIds = buildReferencedAssetIds({
            assetId,
            sources: Array.isArray(scriptFields.sources) ? scriptFields.sources : undefined,
        });

        console.log("[CompositionPersistenceService] updateClip offsetSec:", mapped["offsetSec"]);

        await setDoc(clipRef, {
            id: clip.id,
            compositionId: this.activeCompositionId,
            layerId,
            userId: this.userId,
            ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
            schemaVersion: PERSISTED_SCHEMA_VERSION,
            updatedAt: nowUnixSec(),
            assetId: assetId ?? null,
            thumbnailId: thumbnailId ?? null,
            referencedAssetIds,
            ...mapped,
            ...scriptFields,
        }, {merge: true});
    }

    /**
     * Build a persisted-like clip record from an in-memory engine clip.
     * Used by export to overlay the latest unsynced editor state onto detached restores.
     */
    static serializeClipForExportSnapshot(
        clip: EngineClipLike,
        opts?: { layerId?: string | null }
    ): ExportSnapshotClipRecord | null {
        if (!clip?.id) return null;

        const layerId = String(opts?.layerId ?? clip?.track?.id ?? "");
        if (!layerId) return null;

        const mapped = this.mapClipForStorage(clip);
        const scriptFields = this.mapScriptClipFields(clip);

        let assetId: string | null = this.lookupAssetId(String(clip.id)) ?? null;
        if (!assetId) {
            try {
                const sourceId = (clip as any)?.source?.id;
                if (sourceId) assetId = String(sourceId);
            } catch {}
        }
        if (!assetId && String(clip.type ?? "") === "script") {
            try {
                const cfgs: any[] =
                    typeof (clip as any)?.getInputConfigs === "function" ? (clip as any).getInputConfigs() : [];
                const first = cfgs.find((c: any) => typeof c?.assetId === "string" && c.assetId);
                if (first?.assetId) assetId = String(first.assetId);
            } catch {}
        }

        let thumbnailId: string | null = this.lookupThumbnailId(String(clip.id)) ?? null;
        if (!thumbnailId) {
            try {
                const fromStore = (CompositionStateStore as any)?.thumbnailIdByClipId?.[String(clip.id)];
                if (fromStore) thumbnailId = String(fromStore);
            } catch {}
        }

        return {
            id: String(clip.id),
            layerId,
            type: String(clip.type ?? "unknown"),
            name: String((clip as any)?.name ?? ""),
            assetId,
            thumbnailId,
            ...mapped,
            ...scriptFields,
        };
    }

    /**
     * Update only the script-specific fields (cuts, gaps, splits) of a ScriptLayerClip.
     * Use this when only edits have changed (not timing).
     * Uses setDoc with merge so it creates the document if it doesn't exist.
     */
    static async updateScriptClipEdits(clipId: string, layerId: string, edits: {
        scriptCuts?: ScriptLayerCut[];
        gaps?: ScriptLayerGap[];
        gapInsertions?: ScriptLayerGapInsertion[];
        splits?: ScriptLayerSplit[];
        speakerOverrides?: SpeakerOverride[];
        speakerMuteRanges?: SpeakerMuteRange[];
        transcriptHiddenRanges?: TranscriptHiddenRange[];
        wordCorrections?: TranscriptWordCorrection[];
        transcriptParagraphBreaks?: TranscriptParagraphBreak[];
        transcriptMarkers?: TranscriptMarker[];
    }): Promise<void> {
        if (!this.activeCompositionId || !clipId || !layerId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping updateScriptClipEdits while writes are disabled");
            return;
        }
        // Script edits imply a script clip exists, so this composition is no longer "new/empty".
        await this.markCompositionNonEmptyOnce();

        // Update the clip with script edits
        const clipRef = doc(collection(doc(collection(doc(db, kCompositionsCollectionName, this.activeCompositionId), kCompositionLayersSubcollectionName), layerId), kCompositionLayerClipsSubcollectionName), clipId);
        const updates: Record<string, any> = {
            id: clipId,
            compositionId: this.activeCompositionId,
            layerId,
            userId: this.userId,
            ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
            schemaVersion: PERSISTED_SCHEMA_VERSION,
            updatedAt: nowUnixSec()
        };
        if (edits.scriptCuts !== undefined) updates.scriptCuts = this.serializeCuts(edits.scriptCuts);
        if (edits.gaps !== undefined) updates.gaps = this.serializeGaps(edits.gaps);
        if (edits.gapInsertions !== undefined) updates.gapInsertions = this.serializeGapInsertions(edits.gapInsertions);
        if (edits.splits !== undefined) updates.splits = this.serializeSplits(edits.splits);
        if (edits.speakerOverrides !== undefined) updates.speakerOverrides = this.serializeSpeakerOverrides(edits.speakerOverrides);
        if (edits.speakerMuteRanges !== undefined) updates.speakerMuteRanges = this.serializeSpeakerMuteRanges(edits.speakerMuteRanges);
        if (edits.transcriptHiddenRanges !== undefined) updates.transcriptHiddenRanges = this.serializeTranscriptHiddenRanges(edits.transcriptHiddenRanges);
        if (edits.wordCorrections !== undefined) updates.wordCorrections = this.serializeWordCorrections(edits.wordCorrections);
        if (edits.transcriptParagraphBreaks !== undefined) updates.transcriptParagraphBreaks = this.serializeTranscriptParagraphBreaks(edits.transcriptParagraphBreaks);
        if (edits.transcriptMarkers !== undefined) updates.transcriptMarkers = this.serializeTranscriptMarkers(edits.transcriptMarkers);

        await setDoc(clipRef, updates, {merge: true});
    }

    // Track pending deletes to prevent duplicate calls
    private static pendingClipDeletes = new Set<string>();

    /**
     * Delete a clip from a layer.
     * Silently succeeds if the clip doesn't exist in Firestore.
     */
    static async deleteClip(layerId: string, clipId: string): Promise<void> {
        if (!this.activeCompositionId || !layerId || !clipId || !this.userId) return;
        if (this.isTearingDown || this.readOnly) {
            console.log("[CompositionPersistenceService] Skipping clip delete while writes are disabled:", clipId);
            return;
        }

        const deleteKey = `${this.activeCompositionId}/${layerId}/${clipId}`;
        if (this.pendingClipDeletes.has(deleteKey)) {
            console.warn("[CompositionPersistenceService] Duplicate delete call ignored for clip:", clipId);
            return;
        }
        this.pendingClipDeletes.add(deleteKey);

        const clipRef = doc(
            db,
            kCompositionsCollectionName, this.activeCompositionId,
            kCompositionLayersSubcollectionName, layerId,
            kCompositionLayerClipsSubcollectionName, clipId
        );

        try {
            await deleteDoc(clipRef);
            console.log("[CompositionPersistenceService] Clip deleted successfully:", clipId);
        } catch (e: any) {
            // Silently ignore permission errors for non-existent docs
            if (e?.code === 'permission-denied' || e?.message?.includes('permission')) {
                console.log('[CompositionPersistenceService] Clip not in Firestore, skipping delete:', clipId);
                return;
            }
            console.error("[CompositionPersistenceService] Error deleting clip:", e);
        } finally {
            this.pendingClipDeletes.delete(deleteKey);
        }
    }

    // ============ Fetch/Restore Operations ============

    /**
     * Fetch composition metadata (without layers/clips).
     */
    static async fetchComposition(compositionId?: string): Promise<{
        compositionId: string;
        projectId: string;
        title: string;
        schemaVersion: number;
        canvasWidth: number;
        canvasHeight: number;
        backgroundColor: string;
        orderedLayerIds: string[];
    } | null> {
        const cid = compositionId ?? this.activeCompositionId;
        if (!cid || !this.userId) return null;

        try {
            const compRef = doc(db, kCompositionsCollectionName, cid);
            const compSnap = await getDoc(compRef);
            if (!compSnap.exists()) return null;

            const data = compSnap.data() || {};
            return {
                compositionId: data.compositionId ?? cid,
                projectId: data.projectId ?? '',
                title: data.title ?? 'Untitled',
                schemaVersion: Number(data.schemaVersion ?? 0),
                canvasWidth: Number(data.canvasWidth ?? 1920),
                canvasHeight: Number(data.canvasHeight ?? 1080),
                backgroundColor: String(data.backgroundColor ?? '#000000'),
                orderedLayerIds: Array.isArray(data.orderedLayerIds) ? data.orderedLayerIds : [],
            };
        } catch (e) {
            console.error('[CompositionPersistenceService] Error fetching composition:', e);
            return null;
        }
    }

    /**
     * Fetch all layers and clips for a composition, ordered by orderedLayerIds.
     * This is the main method for restoring a composition from persistence.
     */
    static async fetchClipsByLayerOrdered(compositionId?: string): Promise<{
        layers: Array<{
            layerId: string;
            name: string | null;
            type: string;
            clips: any[];
        }>;
    }> {
        const cid = compositionId ?? this.activeCompositionId;
        if (!cid || !this.userId) return {layers: []};

        try {
            const compRef = doc(db, kCompositionsCollectionName, cid);
            let compSnap;
            try {
                compSnap = await getDoc(compRef);
            } catch (e) {
                console.error('[CompositionPersistenceService] Error fetching composition doc:', {compositionId: cid}, e);
                return {layers: []};
            }
            if (!compSnap.exists()) return {layers: []};

            const compData = compSnap.data() || {};

            const orderedLayerIds: string[] = Array.isArray(compData.orderedLayerIds) ? compData.orderedLayerIds : [];
            if (!orderedLayerIds.length) return {layers: []};

            const layers: Array<{ layerId: string; name: string | null; type: string; clips: any[] }> = [];

            for (const layerId of orderedLayerIds) {
                const layerDocRef = doc(collection(compRef, kCompositionLayersSubcollectionName), layerId);

                let layerSnap;
                try {
                    layerSnap = await getDoc(layerDocRef);
                } catch (e) {
                    console.warn('[CompositionPersistenceService] Error fetching layer doc:', {compositionId: cid, layerId, path: layerDocRef.path}, e);
                    continue;
                }
                if (!layerSnap.exists()) continue;

                const layerData = layerSnap.data() || {};

                const layerName = layerData.name ?? null;
                const layerType = layerData.type ?? 'default';

                const clipsCol = collection(layerDocRef, kCompositionLayerClipsSubcollectionName);
                const orderedClipIds: string[] = Array.isArray(layerData.orderedClipIds)
                    ? layerData.orderedClipIds.filter((id: any) => typeof id === 'string' && id.length > 0)
                    : [];

	                let clips: any[] = [];
	                if (orderedClipIds.length > 0) {
	                    const seen = new Set<string>();
	                    for (const clipId of orderedClipIds) {
	                        if (seen.has(clipId)) continue;
	                        seen.add(clipId);

	                        try {
	                            const clipDocRef = doc(clipsCol, clipId);
	                            const clipSnap = await getDoc(clipDocRef);
	                            if (!clipSnap.exists()) continue;
	                            const c = clipSnap.data() || {};
	                            // Return the full persisted clip record so restore can consume clip-type-specific config
	                            // without needing to update this fetch method each time we add a new persisted field.
	                            clips.push({
	                                ...c,
	                                id: c.id ?? clipSnap.id,
	                                layerId,
	                                type: c.type ?? "unknown",
	                                startSec: Number(c.startSec ?? 0),
	                                inSec: Number(c.inSec ?? 0),
	                                outSec: Number(c.outSec ?? 0),
	                                offsetSec: Number(c.offsetSec ?? 0),
	                                durationSec: Number(c.durationSec ?? 0),
	                                trimmedDurationSec: Number(c.trimmedDurationSec ?? 0),
	                            });
	                        } catch (e) {
	                            console.warn('[CompositionPersistenceService] Error fetching clip doc:', {compositionId: cid, layerId, clipId}, e);
	                        }
	                    }
	                } else {
                    // Back-compat for older layers that don't have orderedClipIds yet.
                    let snaps;
                    try {
                        snaps = await getDocs(clipsCol);
                    } catch (e) {
                        console.warn('[CompositionPersistenceService] Error fetching layer clips:', {compositionId: cid, layerId, path: clipsCol.path}, e);
                        continue;
                    }

	                    clips = snaps.docs.map(d => {
	                        const c = d.data() || {};
	                        return {
	                            ...c,
	                            id: c.id ?? d.id,
	                            layerId,
	                            type: c.type ?? "unknown",
	                            startSec: Number(c.startSec ?? 0),
	                            inSec: Number(c.inSec ?? 0),
	                            outSec: Number(c.outSec ?? 0),
	                            offsetSec: Number(c.offsetSec ?? 0),
	                            durationSec: Number(c.durationSec ?? 0),
	                            trimmedDurationSec: Number(c.trimmedDurationSec ?? 0),
	                        };
	                    });
	                }

                // Sort clips by startSec
                clips.sort((a, b) => a.startSec - b.startSec);

                layers.push({layerId, name: layerName, type: layerType, clips});
            }

            return {layers};
        } catch (e) {
            console.error('[CompositionPersistenceService] Error fetching clips by layer:', e);
            return {layers: []};
        }
    }

    // ============ Private Helpers ============

    /**
     * Map an engine clip to the storage format (timing + props).
     */
    private static mapClipForStorage(clip: EngineClipLike): Record<string, any> {
        const isMedia = ('range' in clip) && ('offset' in clip);
        let startSec = 0;
        let durationSec = 0;
        let trimmedDurationSec = 0;
        let inSec = 0;
        let outSec = 0;
        let offsetSec = 0;
        if (isMedia) {
            inSec = Number(clip.range?.[0]?.seconds ?? 0);
            outSec = Number(clip.range?.[1]?.seconds ?? inSec);
            offsetSec = Number(clip.offset?.seconds ?? 0);
            startSec = Number(clip.start?.seconds ?? 0);
            durationSec = Number(clip.duration?.seconds ?? Math.max(0, outSec - inSec));
            trimmedDurationSec = Number(clip.timelineDuration?.seconds ?? Math.max(0, outSec - inSec));
        } else {
            const s = Number(clip.start?.seconds ?? 0);
            const e = Number(clip.stop?.seconds ?? s);
            startSec = s;
            durationSec = Math.max(0, e - s);
            trimmedDurationSec = Number(clip.timelineDuration?.seconds ?? durationSec);
            inSec = 0;
            outSec = durationSec;
            offsetSec = 0;
        }
        const result: Record<string, any> = {
            startSec,
            durationSec,
            trimmedDurationSec,
            inSec,
            outSec,
            offsetSec,
            props: {
                playbackRate: Number(clip.playbackRate ?? 1),
                gain: Number(clip.gain ?? 1),
                alpha: Number(clip.alpha ?? 1),
            },
            media: {
                hasAudioStream: Boolean((clip as any).hasAudioStream),
                hasVideoStream: Boolean((clip as any).hasVideoStream),
            },
        };

        // Audio transitions (fade in/out and internal transition points) for MediaClip-derived clips.
        try {
            const fadeIn = (clip as any)?.fadeIn;
            const fadeOut = (clip as any)?.fadeOut;
            result.fadeIn = fadeIn
                ? { type: String(fadeIn.type ?? "fade"), durationMs: Number(fadeIn.durationMs ?? 0) }
                : null;
            result.fadeOut = fadeOut
                ? { type: String(fadeOut.type ?? "fade"), durationMs: Number(fadeOut.durationMs ?? 0) }
                : null;

            const tps: any[] = Array.isArray((clip as any)?.transitionPoints)
                ? (clip as any).transitionPoints
                : [];
            result.transitionPoints = tps.map((tp: any) => {
                const fadeOutCfg = tp?.transition?.fadeOut;
                const fadeInCfg = tp?.transition?.fadeIn;
                return {
                    id: String(tp?.id ?? ""),
                    srcTimeSec: Number(tp?.srcTimeSec ?? 0),
                    transition: {
                        fadeOut: fadeOutCfg
                            ? { type: String(fadeOutCfg.type ?? "none"), durationMs: Number(fadeOutCfg.durationMs ?? 0) }
                            : null,
                        fadeIn: fadeInCfg
                            ? { type: String(fadeInCfg.type ?? "none"), durationMs: Number(fadeInCfg.durationMs ?? 0) }
                            : null,
                    },
                };
            });
        } catch {}

        // Visual transitions (fade in/out) for VideoClip/ImageClip (best-effort).
        try {
            const vfi = (clip as any)?.visualFadeIn;
            const vfo = (clip as any)?.visualFadeOut;

            const mapVisual = (cfg: any): any | null => {
                if (!cfg || typeof cfg.type !== "string") return null;
                return {
                    type: String(cfg.type),
                    durationMs: Number(cfg.durationMs ?? 0),
                    wipeDirection: typeof cfg.wipeDirection === "string" ? cfg.wipeDirection : null,
                    blurAmount: Number.isFinite(cfg.blurAmount) ? Number(cfg.blurAmount) : null,
                };
            };

            result.visualFadeIn = mapVisual(vfi);
            result.visualFadeOut = mapVisual(vfo);
        } catch {}

        // Studio Sound (denoised audio) state where supported (AudioClip/VideoClip/ScriptLayerClip).
        try {
            const ss = (clip as any)?.studioSoundEnabled;
            if (typeof ss === "boolean") {
                result.studioSoundEnabled = ss;
            }
        } catch {}

        // Visual props (position, size, crop, transforms).
        try {
            const visual: Record<string, any> = {};
            const type = String((clip as any)?.type ?? "");
            const isCaptions = type === "captions";
            const isText = type === "text";

            const pos = (clip as any)?.position;
            const x =
                typeof pos?.x === "number"
                    ? pos.x
                    : typeof (clip as any)?.x === "number"
                      ? (clip as any).x
                      : undefined;
            const y =
                typeof pos?.y === "number"
                    ? pos.y
                    : typeof (clip as any)?.y === "number"
                      ? (clip as any).y
                      : undefined;
            if (typeof x === "number" && Number.isFinite(x)) visual.x = x;
            if (typeof y === "number" && Number.isFinite(y)) visual.y = y;

            // Captions/Text are positioned text; do not persist width/height/scale derived from bounds
            // (it would re-apply scaling via @visualize and distort font sizes on restore).
            if (!isCaptions && !isText) {
                const w = (clip as any)?.width;
                const h = (clip as any)?.height;
                if (typeof w === "number" && Number.isFinite(w) && w > 0) visual.width = w;
                if (typeof h === "number" && Number.isFinite(h) && h > 0) visual.height = h;

                const r = (clip as any)?.rotation;
                if (typeof r === "number" && Number.isFinite(r)) visual.rotation = r;

                const maskShape = (clip as any)?.maskShape;
                if (typeof maskShape === "string" && maskShape && maskShape !== "none") visual.maskShape = maskShape;

                // VisualMixin exposes `scale` as `_scale ?? view.scale`.
                // Persist only the explicit `_scale` (additional multiplier), not the derived view.scale,
                // otherwise restore can double-apply and inflate/shrink clips.
                const scale = (clip as any)?._scale;
                const sx = typeof scale?.x === "number" ? scale.x : undefined;
                const sy = typeof scale?.y === "number" ? scale.y : undefined;
                const hasScaleX = typeof sx === "number" && Number.isFinite(sx);
                const hasScaleY = typeof sy === "number" && Number.isFinite(sy);
                if (hasScaleX) visual.scaleX = sx;
                if (hasScaleY) visual.scaleY = sy;
                if (hasScaleX || hasScaleY) {
                    // Marks persisted scale as VisualMixin's "relative/additional" multiplier.
                    // Restore can treat legacy records (derived view.scale) differently.
                    (visual as any).scaleIsRelative = true;
                }

                if (typeof (clip as any)?.getCrop === "function") {
                    const c = (clip as any).getCrop();
                    if (
                        c &&
                        typeof c.x === "number" &&
                        typeof c.y === "number" &&
                        typeof c.width === "number" &&
                        typeof c.height === "number" &&
                        Number.isFinite(c.x) &&
                        Number.isFinite(c.y) &&
                        Number.isFinite(c.width) &&
                        Number.isFinite(c.height)
                    ) {
                        visual.crop = { x: c.x, y: c.y, width: c.width, height: c.height };
                    }
                }
            }

            if (Object.keys(visual).length > 0) {
                result.visual = visual;
            }
        } catch {}

        // Captions (text overlay) config (do not persist words).
        try {
            if (String((clip as any)?.type ?? "") === "captions") {
                const c: any = clip;
                const rawSceneOverrides = c?.sceneStyleOverrides;
                let sceneStyleOverrides: any = rawSceneOverrides ?? null;
                if (rawSceneOverrides && typeof rawSceneOverrides === "object") {
                    const mapped: Record<string, any> = {};
                    for (const [k, v] of Object.entries(rawSceneOverrides)) {
                        if (!k || typeof k !== "string") continue;
                        mapped[this.toFirestoreSceneKey(k)] = v;
                    }
                    sceneStyleOverrides = mapped;
                }
                result.captions = {
                    scriptClipId: c?.scriptClipId ?? null,
                    timingScope: c?.timingScope ?? null,
                    displayMode: c?.displayMode ?? null,
                    textAlign: c?.textAlign ?? null,
                    textBaseline: c?.textBaseline ?? null,
                    textCase: c?.textCase ?? null,
                    maxWidth: c?.maxWidth ?? null,
                    textStyle: c?.textStyle ?? null,
                    highlightStyle: c?.highlightStyle ?? null,
                    background: c?.background ?? null,
                    phraseConfig: c?.phraseConfig ?? null,
                    sceneStyleOverrides,
                };
            }
        } catch {}

        // Text (user-defined text overlay) config.
        try {
            if (String((clip as any)?.type ?? "") === "text") {
                const c: any = clip;
                const rawSceneOverrides = c?.sceneStyleOverrides;
                let sceneStyleOverrides: any = rawSceneOverrides ?? null;
                if (rawSceneOverrides && typeof rawSceneOverrides === "object") {
                    const mapped: Record<string, any> = {};
                    for (const [k, v] of Object.entries(rawSceneOverrides)) {
                        if (!k || typeof k !== "string") continue;
                        mapped[this.toFirestoreSceneKey(k)] = v;
                    }
                    sceneStyleOverrides = mapped;
                }
                result.text = {
                    text: c?.text ?? null,
                    textAlign: c?.textAlign ?? null,
                    textBaseline: c?.textBaseline ?? null,
                    textCase: c?.textCase ?? null,
                    maxWidth: c?.maxWidth ?? null,
                    textStyle: c?.textStyle ?? null,
                    sceneStyleOverrides,
                };
            }
        } catch {}

        // Placeholder (layout slots / replaceable media) config.
        try {
            if (String((clip as any)?.type ?? "") === "placeholder") {
                const c: any = clip;
                result.placeholder = {
                    label: typeof c?.label === "string" ? c.label : null,
                    placeholderKind: typeof c?.placeholderKind === "string" ? c.placeholderKind : null,
                };
            }
        } catch {}

        // Per-scene visual overrides for non-script visual clips.
        // Stored per canvas aspect preset to keep independent framing in 16:9 / 9:16 / 1:1.
        // Keyed by script scene startSplitId (or "__start__").
        try {
            const byAspect = (clip as any)?.sceneVisualOverridesByAspect;
            if (byAspect && typeof byAspect === "object") {
                const outByAspect: Record<string, any> = {};
                for (const presetKey of ["horizontal", "vertical", "square"]) {
                    const ovs = (byAspect as any)[presetKey];
                    if (!ovs || typeof ovs !== "object") continue;
                    const cleaned: Record<string, any> = {};
                    for (const [k, v] of Object.entries(ovs)) {
                        if (!k || typeof k !== "string") continue;
                        if (!v || typeof v !== "object") continue;
                        const out: any = {};
                        for (const key of ["x", "y", "width", "height", "alpha", "rotation"]) {
                            const num = (v as any)[key];
                            if (typeof num === "number" && Number.isFinite(num)) out[key] = num;
                        }
                        if (typeof (v as any)?.hiddenByUser === "boolean" && (v as any).hiddenByUser === true) {
                            out.hiddenByUser = true;
                        }
                        const maskShape = (v as any).maskShape;
                        if (typeof maskShape === "string" && maskShape) out.maskShape = maskShape;
                        try {
                            const crop = (v as any)?.crop;
                            if (crop && typeof crop === "object") {
                                const x = Number((crop as any).x);
                                const y = Number((crop as any).y);
                                const width = Number((crop as any).width);
                                const height = Number((crop as any).height);
                                if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                    out.crop = { x, y, width, height };
                                }
                            }
                        } catch {}
                        if (Object.keys(out).length > 0) cleaned[this.toFirestoreSceneKey(k)] = out;
                    }
                    if (Object.keys(cleaned).length > 0) outByAspect[presetKey] = cleaned;
                }
                if (Object.keys(outByAspect).length > 0) {
                    result.sceneVisualOverridesByAspect = outByAspect;
                }
            } else {
                // Legacy fallback: if a clip still has single-map overrides, persist them under all presets.
                const ovs = (clip as any)?.sceneVisualOverrides;
                if (ovs && typeof ovs === "object") {
                    const cleaned: Record<string, any> = {};
                    for (const [k, v] of Object.entries(ovs)) {
                        if (!k || typeof k !== "string") continue;
                        if (!v || typeof v !== "object") continue;
                        const out: any = {};
                        for (const key of ["x", "y", "width", "height", "alpha", "rotation"]) {
                            const num = (v as any)[key];
                            if (typeof num === "number" && Number.isFinite(num)) out[key] = num;
                        }
                        if (typeof (v as any)?.hiddenByUser === "boolean" && (v as any).hiddenByUser === true) {
                            out.hiddenByUser = true;
                        }
                        const maskShape = (v as any).maskShape;
                        if (typeof maskShape === "string" && maskShape) out.maskShape = maskShape;
                        if (Object.keys(out).length > 0) cleaned[this.toFirestoreSceneKey(k)] = out;
                    }
                    if (Object.keys(cleaned).length > 0) {
                        result.sceneVisualOverridesByAspect = {
                            horizontal: cleaned,
                            vertical: cleaned,
                            square: cleaned,
                        };
                    }
                }
            }
        } catch {}

        return result;
    }

    /**
     * Map ScriptLayerClip-specific fields for storage.
     * Returns empty object for non-script clips.
     */
    private static mapScriptClipFields(clip: EngineClipLike): Record<string, any> {
        if (clip.type !== 'script') return {};

        return {
            scriptCuts: this.serializeCuts(clip.scriptCuts ?? []),
            gaps: this.serializeGaps(clip.gaps ?? []),
            gapInsertions: this.serializeGapInsertions((clip as any).gapInsertions ?? []),
            splits: this.serializeSplits(clip.splits ?? []),
            speakerOverrides: this.serializeSpeakerOverrides(clip.speakerOverrides ?? []),
            speakerMuteRanges: this.serializeSpeakerMuteRanges((clip as any).speakerMuteRanges ?? []),
            transcriptHiddenRanges: this.serializeTranscriptHiddenRanges((clip as any).transcriptHiddenRanges ?? []),
            wordCorrections: this.serializeWordCorrections((clip as any).wordCorrections ?? []),
            transcriptParagraphBreaks: this.serializeTranscriptParagraphBreaks((clip as any).transcriptParagraphBreaks ?? []),
            transcriptMarkers: this.serializeTranscriptMarkers((clip as any).transcriptMarkers ?? []),
            multicamCameraSetup: this.serializeMulticamCameraSetup((clip as any).multicamCameraSetup),
            sources: this.serializeSources(clip),
            sceneProperties: this.serializeSceneProperties(clip),
        };
    }

    private static serializeMulticamCameraSetup(setupRaw: any): any | null {
        if (!setupRaw || typeof setupRaw !== "object") return null;

        const version = 1;
        const assignmentsRaw = Array.isArray((setupRaw as any).assignments) ? (setupRaw as any).assignments : [];
        const out: any[] = [];

        for (const a of assignmentsRaw) {
            const sourceIndex = Number((a as any)?.sourceIndex);
            if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
            const kind = String((a as any)?.kind ?? "").trim();
            if (kind !== "speaker" && kind !== "shared" && kind !== "unassigned") continue;
            const rec: any = { sourceIndex, kind };
            const speakerId = String((a as any)?.speakerId ?? "").trim();
            if (kind === "speaker" && speakerId) rec.speakerId = speakerId;
            out.push(rec);
        }

        out.sort((a, b) => Number(a?.sourceIndex ?? 0) - Number(b?.sourceIndex ?? 0));
        if (out.length === 0) return null;
        return { version, assignments: out };
    }

    private static serializeTranscriptHiddenRanges(ranges: TranscriptHiddenRange[]): any[] {
        const out: any[] = [];
        for (const r of ranges ?? []) {
            const id = String((r as any)?.id ?? "");
            const startSec = Number((r as any)?.startSec ?? 0);
            const endSec = Number((r as any)?.endSec ?? 0);
            if (!id || !Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
            out.push({ id, startSec, endSec });
        }
        out.sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));
        return out;
    }

    private static serializeWordCorrections(corrections: TranscriptWordCorrection[]): any[] {
        const out: any[] = [];
        for (const c of corrections ?? []) {
            const id = String((c as any)?.id ?? "");
            const correctedText = String((c as any)?.correctedText ?? "").trim();
            if (!id || !correctedText) continue;
            const rec: any = { id, correctedText };
            if (Number.isFinite((c as any)?.sourceIndex)) rec.sourceIndex = Number((c as any).sourceIndex);
            if (Number.isFinite((c as any)?.srcStartSec)) rec.srcStartSec = Number((c as any).srcStartSec);
            if (Number.isFinite((c as any)?.srcEndSec)) rec.srcEndSec = Number((c as any).srcEndSec);
            out.push(rec);
        }
        out.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
        return out;
    }

    private static serializeTranscriptParagraphBreaks(breaks: TranscriptParagraphBreak[]): any[] {
        const out: any[] = [];
        for (const b of breaks ?? []) {
            const id = String((b as any)?.id ?? "");
            if (!id) continue;
            const rec: any = { id };
            if (Number.isFinite((b as any)?.sourceIndex)) rec.sourceIndex = Number((b as any).sourceIndex);
            if (Number.isFinite((b as any)?.srcStartSec)) rec.srcStartSec = Number((b as any).srcStartSec);
            if (Number.isFinite((b as any)?.srcEndSec)) rec.srcEndSec = Number((b as any).srcEndSec);
            if (Number.isFinite((b as any)?.timeSec)) rec.timeSec = Number((b as any).timeSec);
            out.push(rec);
        }
        out.sort((a, b) => (a.timeSec ?? 0) - (b.timeSec ?? 0));
        return out;
    }

    private static serializeTranscriptMarkers(markers: TranscriptMarker[]): any[] {
        const out: any[] = [];

        const cleanAnchor = (a: any): any | undefined => {
            if (!a || typeof a !== "object") return undefined;
            const rec: any = {};
            if (Number.isFinite(a?.sourceIndex)) rec.sourceIndex = Number(a.sourceIndex);
            if (Number.isFinite(a?.srcStartSec)) rec.srcStartSec = Number(a.srcStartSec);
            if (Number.isFinite(a?.srcEndSec)) rec.srcEndSec = Number(a.srcEndSec);
            if (Number.isFinite(a?.timeSec)) rec.timeSec = Number(a.timeSec);
            return Object.keys(rec).length ? rec : undefined;
        };

        for (const m of markers ?? []) {
            const id = String((m as any)?.id ?? "");
            const title = String((m as any)?.title ?? "").trim();
            if (!id || !title) continue;

            const rec: any = { id, title };
            const origin = String((m as any)?.origin ?? "").trim();
            if (origin) rec.origin = origin;
            const after = cleanAnchor((m as any)?.after);
            const before = cleanAnchor((m as any)?.before);
            if (after) rec.after = after;
            if (before) rec.before = before;
            if (Number.isFinite((m as any)?.timeSec)) rec.timeSec = Number((m as any).timeSec);
            if (Number.isFinite((m as any)?.createdAt)) rec.createdAt = Number((m as any).createdAt);
            if (Number.isFinite((m as any)?.updatedAt)) rec.updatedAt = Number((m as any).updatedAt);
            out.push(rec);
        }

        out.sort((a, b) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0));
        return out;
    }

    /**
     * Serialize ScriptLayerClip sceneProperties (Map<number, SceneProperties>) for Firestore.
     * Firestore doesn't accept Map, so we store an array of {sceneIndex, ...props}.
     */
    private static serializeSceneProperties(clip: EngineClipLike): any[] {
        const map: Map<number, SceneProperties> | undefined = (clip as any)?.sceneProperties;
        if (!map || typeof (map as any).entries !== "function") return [];

        const out: any[] = [];

        for (const [sceneIndexRaw, propsRaw] of (map as any).entries()) {
            const sceneIndex = Number(sceneIndexRaw);
            if (!Number.isFinite(sceneIndex)) continue;

            const props = (propsRaw ?? {}) as SceneProperties;
            const rec: Record<string, any> = { sceneIndex };

            if (props.gain !== undefined && Number.isFinite(props.gain)) rec.gain = Number(props.gain);
            if (props.playbackRate !== undefined && Number.isFinite(props.playbackRate)) rec.playbackRate = Number(props.playbackRate);
            if (typeof (props as any)?.backgroundColor === "string" && String((props as any).backgroundColor).trim()) {
                rec.backgroundColor = String((props as any).backgroundColor).trim();
            }
            // Per-source audio gain multipliers (scene-specific mute/mix)
            try {
                const audioSourceGains = (props as any)?.audioSourceGains;
                if (audioSourceGains && typeof audioSourceGains?.entries === "function") {
                    const arr: any[] = [];
                    for (const [sourceIndexRaw, gainRaw] of audioSourceGains.entries()) {
                        const sourceIndex = Number(sourceIndexRaw);
                        const gain = Number(gainRaw);
                        if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                        if (!Number.isFinite(gain)) continue;
                        arr.push({ sourceIndex, gain });
                    }
                    arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                    if (arr.length > 0) rec.audioSourceGains = arr;
                }
            } catch {}
            if (props.layout !== undefined && props.layout !== null) rec.layout = String(props.layout);
            try {
                const byAspect = (props as any)?.layoutByAspect;
                if (byAspect && typeof byAspect === "object") {
                    const cleaned: any = {};
                    for (const key of ["horizontal", "vertical", "square"]) {
                        if (!Object.prototype.hasOwnProperty.call(byAspect, key)) continue;
                        const v = (byAspect as any)[key];
                        if (v === null) cleaned[key] = null;
                        else if (typeof v === "string" && v) cleaned[key] = v;
                    }
                    if (Object.keys(cleaned).length > 0) rec.layoutByAspect = cleaned;
                }
            } catch {}
            if (props.inTransition) rec.inTransition = this.serializeTransition(props.inTransition);
            if (props.outTransition) rec.outTransition = this.serializeTransition(props.outTransition);

            const visualOverrides = props.visualOverrides;
            if (visualOverrides && typeof (visualOverrides as any).entries === "function") {
                const vis: any[] = [];
                for (const [sourceIndexRaw, vRaw] of (visualOverrides as any).entries()) {
                    const sourceIndex = Number(sourceIndexRaw);
                    if (!Number.isFinite(sourceIndex)) continue;
                    const v = (vRaw ?? {}) as SceneVisualProperties;

                    const vrec: Record<string, any> = { sourceIndex };
                    if (v.alpha !== undefined && Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                    if (typeof (v as any)?.hiddenByUser === "boolean" && (v as any).hiddenByUser === true) {
                        vrec.hiddenByUser = true;
                    }
                    if (v.x !== undefined && Number.isFinite(v.x)) vrec.x = Number(v.x);
                    if (v.y !== undefined && Number.isFinite(v.y)) vrec.y = Number(v.y);
                    if (v.width !== undefined && Number.isFinite(v.width)) vrec.width = Number(v.width);
                    if (v.height !== undefined && Number.isFinite(v.height)) vrec.height = Number(v.height);
                    try {
                        const crop = (v as any)?.crop;
                        if (crop && typeof crop === "object") {
                            const x = Number((crop as any).x);
                            const y = Number((crop as any).y);
                            const width = Number((crop as any).width);
                            const height = Number((crop as any).height);
                            if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                vrec.crop = { x, y, width, height };
                            }
                        }
                    } catch {}
                    if (typeof (v as any).maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                    if (Object.keys(vrec).length > 1) vis.push(vrec);
                }
                vis.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                if (vis.length > 0) rec.visualOverrides = vis;
            }

            // Per-aspect visual overrides
            try {
                const byAspect = (props as any)?.visualOverridesByAspect;
                if (byAspect && typeof byAspect === "object") {
                    const outByAspect: any = {};
                    for (const [presetKey, mapRaw] of Object.entries(byAspect as any)) {
                        const m: any = mapRaw as any;
                        if (!m || typeof m?.entries !== "function") continue;
                        const arr: any[] = [];
                        for (const [sourceIndexRaw, vRaw] of m.entries()) {
                            const sourceIndex = Number(sourceIndexRaw);
                            if (!Number.isFinite(sourceIndex)) continue;
                            const v = (vRaw ?? {}) as SceneVisualProperties;
                            const vrec: Record<string, any> = { sourceIndex };
                            if (v.alpha !== undefined && Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                            if (typeof (v as any)?.hiddenByUser === "boolean" && (v as any).hiddenByUser === true) {
                                vrec.hiddenByUser = true;
                            }
                            if (v.x !== undefined && Number.isFinite(v.x)) vrec.x = Number(v.x);
                            if (v.y !== undefined && Number.isFinite(v.y)) vrec.y = Number(v.y);
                            if (v.width !== undefined && Number.isFinite(v.width)) vrec.width = Number(v.width);
                            if (v.height !== undefined && Number.isFinite(v.height)) vrec.height = Number(v.height);
                            try {
                                const crop = (v as any)?.crop;
                                if (crop && typeof crop === "object") {
                                    const x = Number((crop as any).x);
                                    const y = Number((crop as any).y);
                                    const width = Number((crop as any).width);
                                    const height = Number((crop as any).height);
                                    if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                        vrec.crop = { x, y, width, height };
                                    }
                                }
                            } catch {}
                            if (typeof (v as any).maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                            if (Object.keys(vrec).length > 1) arr.push(vrec);
                        }
                        arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                        if (arr.length > 0) outByAspect[String(presetKey)] = arr;
                    }
                    if (Object.keys(outByAspect).length > 0) rec.visualOverridesByAspect = outByAspect;
                }
            } catch {}

            if (Object.keys(rec).length > 1) out.push(rec);
        }

        out.sort((a, b) => (a.sceneIndex ?? 0) - (b.sceneIndex ?? 0));
        return out;
    }

    private static serializeTransition(t: TransitionConfig): any {
        return {
            audio: t.audio ? { type: t.audio.type, durationMs: t.audio.durationMs } : null,
            video: t.video
                ? {
                      type: t.video.type,
                      durationMs: t.video.durationMs,
                      easing: typeof (t.video as any)?.easing === "string" ? (t.video as any).easing : null,
                      addFades: typeof (t.video as any)?.addFades === "boolean" ? (t.video as any).addFades : null,
                  }
                : null,
        };
    }

    /**
     * Serialize cuts for Firestore (strips non-serializable analysis data).
     */
    private static serializeCuts(cuts: ScriptLayerCut[]): any[] {
        return cuts.map(cut => ({
            id: cut.id,
            startSec: cut.startSec,
            endSec: cut.endSec,
            transcriptMode: cut.transcriptMode ?? 'delete',
            groupId: cut.groupId ?? null,
            // Note: We don't persist `analysis` as it can be recomputed
            // transition config is persisted
            transition: cut.transition ? {
                audio: cut.transition.audio ? {
                    type: cut.transition.audio.type,
                    durationMs: cut.transition.audio.durationMs,
                } : null,
                video: cut.transition.video ? {
                    type: cut.transition.video.type,
                    durationMs: cut.transition.video.durationMs,
                } : null,
            } : null,
        }));
    }

    /**
     * Serialize gaps for Firestore.
     */
    private static serializeGaps(gaps: ScriptLayerGap[]): any[] {
        return gaps.map(gap => ({
            id: gap.id,
            startSec: gap.startSec,
            endSec: gap.endSec,
        }));
    }

    /**
     * Serialize gap insertions for Firestore.
     * Gap insertions ADD time to the timeline and shift downstream content.
     */
    private static serializeGapInsertions(insertions: ScriptLayerGapInsertion[]): any[] {
        return (insertions ?? []).map(ins => ({
            id: ins.id,
            atSec: ins.atSec,
            durationSec: ins.durationSec,
        }));
    }

    /**
     * Serialize splits for Firestore.
     */
    private static serializeSplits(splits: ScriptLayerSplit[]): any[] {
        return splits.map(split => ({
            id: split.id,
            timeSec: split.timeSec,
            isSceneBoundary: split.isSceneBoundary ?? false,
            label: split.label ?? null,
            transition: split.transition ? {
                audio: split.transition.audio ? {
                    type: split.transition.audio.type,
                    durationMs: split.transition.audio.durationMs,
                } : null,
                video: split.transition.video ? {
                    type: split.transition.video.type,
                    durationMs: split.transition.video.durationMs,
                } : null,
            } : null,
        }));
    }

    /**
     * Serialize speaker overrides for Firestore.
     */
    private static serializeSpeakerOverrides(overrides: SpeakerOverride[]): any[] {
        return (overrides ?? []).map(o => ({
            id: o.id,
            startSec: o.startSec,
            endSec: o.endSec,
            sourceIndex: o.sourceIndex ?? null,
            speakerName: o.speakerName,
            color: o.color ?? null,
            speakerId: o.speakerId ?? null,
            diarizationSpeakerId: o.diarizationSpeakerId ?? null,
        }));
    }

    /**
     * Serialize speaker mute ranges for Firestore.
     */
    private static serializeSpeakerMuteRanges(ranges: SpeakerMuteRange[]): any[] {
        return (ranges ?? []).map((r) => ({
            id: r.id,
            startSec: r.startSec,
            endSec: r.endSec,
            sourceIndex: Number.isFinite((r as any)?.sourceIndex) ? Number((r as any).sourceIndex) : null,
            diarizationSpeakerId: (r as any)?.diarizationSpeakerId ?? null,
            speakerId: (r as any)?.speakerId ?? null,
            speakerName: (r as any)?.speakerName ?? null,
        }));
    }

    /**
     * Serialize sources from a ScriptLayerClip.
     * We need to extract the essential info that allows rebuilding the clip.
     * Note: Firestore doesn't accept undefined, so we use null for optional fields.
     */
    private static serializeSources(clip: EngineClipLike): any[] {
        // ScriptLayerClip stores inputConfigs (stable identity/name/color) and derives audioEntries/videoEntries
        // which reflect runtime transforms/crop/z-order. Persist a merged view so restore is faithful.

        const sources: any[] = [];

        const inputConfigs: any[] = (() => {
            try {
                if (typeof (clip as any).getInputConfigs === "function") return (clip as any).getInputConfigs() || [];
            } catch {}
            return Array.isArray((clip as any).inputConfigs) ? (clip as any).inputConfigs : [];
        })();

        const audioEntries: any[] = (() => {
            try {
                if (typeof (clip as any).getAudioEntries === "function") return (clip as any).getAudioEntries() || [];
            } catch {}
            return Array.isArray((clip as any).audioEntries) ? (clip as any).audioEntries : [];
        })();

        const videoEntries: any[] = (() => {
            try {
                if (typeof (clip as any).getVideoEntries === "function") return (clip as any).getVideoEntries() || [];
            } catch {}
            return Array.isArray((clip as any).videoEntries) ? (clip as any).videoEntries : [];
        })();

	        const audioByInput = new Map<any, any>();
	        for (const e of audioEntries) {
	            if (e?.input) audioByInput.set(e.input, e);
	        }

        const videoByInput = new Map<any, any>();
        for (const e of videoEntries) {
            if (e?.input) videoByInput.set(e.input, e);
        }

        // Build in stable inputConfigs order.
        for (let i = 0; i < inputConfigs.length; i++) {
            const cfg = inputConfigs[i] ?? {};
            const assetId = cfg.assetId ? String(cfg.assetId) : "";
            if (!assetId) continue;

	            const a = cfg.input ? audioByInput.get(cfg.input) : undefined;
	            const v = cfg.input ? videoByInput.get(cfg.input) : undefined;

	            const range = a?.range ?? v?.range ?? cfg?.range;
	            const visual = v?.visual ?? cfg?.visual;
	            const segments = (() => {
	                const cfgSegs: any[] =
	                    Array.isArray(cfg?.segments) && cfg.segments.length > 0 ? (cfg.segments as any[]) : [];
	                if (cfgSegs.length > 0) return cfgSegs;
	                const aSegs: any[] =
	                    Array.isArray(a?.segments) && a.segments.length > 0 ? (a.segments as any[]) : [];
	                if (aSegs.length > 0) return aSegs;
	                const vSegs: any[] =
	                    Array.isArray(v?.segments) && v.segments.length > 0 ? (v.segments as any[]) : [];
	                if (vSegs.length > 0) return vSegs;
	                return null;
	            })();

	            sources.push({
	                assetId,
	                name: cfg.name ?? null,
	                color: cfg.color ?? null,
	                gain: Number.isFinite(a?.gain) ? Number(a.gain) : Number.isFinite(cfg?.gain) ? Number(cfg.gain) : 1,
	                playbackRate: Number.isFinite(a?.playbackRate)
	                    ? Number(a.playbackRate)
	                    : Number.isFinite(v?.playbackRate)
	                      ? Number(v.playbackRate)
	                      : Number.isFinite(cfg?.playbackRate)
	                        ? Number(cfg.playbackRate)
	                        : 1,
	                offsetSec: Number.isFinite(a?.offsetSec)
	                    ? Number(a.offsetSec)
	                    : Number.isFinite(v?.offsetSec)
	                      ? Number(v.offsetSec)
	                      : Number.isFinite(cfg?.offsetSec)
	                        ? Number(cfg.offsetSec)
	                        : 0,
	                range: range
	                    ? {
	                          inSec: Number(range.inSec ?? 0),
	                          outSec: Number(range.outSec ?? 0),
	                      }
	                    : null,
	                segments: Array.isArray(segments)
	                    ? segments
	                          .map((s: any) => {
	                              const segId = s?.id ? String(s.id) : null;
	                              const off = Number(s?.offsetSec ?? 0);
	                              const pr = s?.playbackRate;
	                              const segRate = Number.isFinite(pr) ? Number(pr) : null;
	                              const rin = Number(s?.range?.inSec ?? NaN);
	                              const rout = Number(s?.range?.outSec ?? NaN);
	                              if (!Number.isFinite(off) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
	                              return {
	                                  id: segId,
	                                  offsetSec: off,
	                                  playbackRate: segRate,
	                                  range: { inSec: rin, outSec: rout },
	                              };
	                          })
	                          .filter(Boolean)
	                    : null,
		                visual: visual
		                    ? (() => {
		                          const vrec: Record<string, any> = {
		                              x: Number(visual.x ?? 0),
	                              y: Number(visual.y ?? 0),
	                              width: Number(visual.width ?? 0),
	                              height: Number(visual.height ?? 0),
	                              alpha: Number(visual.alpha ?? 1),
	                              maskShape: typeof (visual as any).maskShape === "string" ? String((visual as any).maskShape) : null,
	                              crop: visual.crop
	                                  ? {
	                                        x: Number(visual.crop.x ?? 0),
	                                        y: Number(visual.crop.y ?? 0),
	                                        width: Number(visual.crop.width ?? 1),
	                                        height: Number(visual.crop.height ?? 1),
	                                    }
	                                  : null,
	                          };
	                          if (Number.isFinite(visual.rotation)) vrec.rotation = Number(visual.rotation);
	                          if (Number.isFinite(visual.originalWidth)) vrec.originalWidth = Number(visual.originalWidth);
	                          if (Number.isFinite(visual.originalHeight)) vrec.originalHeight = Number(visual.originalHeight);
	                          return vrec;
	                      })()
	                    : null,
	                layerIndex: Number.isFinite(v?.layerIndex)
	                    ? Number(v.layerIndex)
	                    : Number.isFinite(cfg?.layerIndex)
	                      ? Number(cfg.layerIndex)
	                      : i,
	            });
	        }

        // Backwards-compat fallback: reconstruct from audio entries if configs are missing.
        if (sources.length === 0 && audioEntries.length > 0) {
            for (let i = 0; i < audioEntries.length; i++) {
                const entry = audioEntries[i];
                const assetId = entry?.assetId ? String(entry.assetId) : "";
                if (!assetId) continue;
                sources.push({
                    assetId,
                    name: entry.name ?? null,
                    color: entry.color ?? null,
                    gain: Number(entry.gain ?? 1),
                    playbackRate: Number(entry.playbackRate ?? 1),
                    offsetSec: Number(entry.offsetSec ?? 0),
                    range: entry.range
                        ? {
                              inSec: Number(entry.range.inSec ?? 0),
                              outSec: Number(entry.range.outSec ?? 0),
                          }
                        : null,
                    layerIndex: i,
                });
            }
        }

        return sources;
    }

    /**
     * Lookup assetId for a clip from the store.
     */
    private static lookupAssetId(clipId: string): string | undefined {
        try {
            return CompositionStateStore.clipIdToAssetId?.[clipId];
        } catch {
            return undefined;
        }
    }

    private static lookupThumbnailId(clipId: string): string | undefined {
        try {
            return CompositionStateStore.clipIdToThumbnailId?.[clipId];
        } catch {
            return undefined;
        }
    }
}
